(() => {
  if (customElements.get('niaash-print-studio')) return;
  class PrintStudio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.token = 0;
      this.lang = document.documentElement.lang.startsWith('pl') ? 'pl' : 'en';
      this.form = this.closest('form');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      this.repeatInput = this.querySelector('[data-repeat-mode]');
      this.fileInput = this.form.querySelector('[type=file]');
      this.linkInput = this.form.querySelector('#ns-art-link');
      this.patternInput = this.form.querySelector('#ns-pattern');
      this.status = this.querySelector('[data-preview-status]');
      this.review = this.querySelector('[data-layout-review]');
      this.hiddenStatus = this.querySelector('[data-preview-property]');
      this.mode = () => this.form.querySelector('[name="ns-artwork-mode"]:checked')?.value;
      const on = (el, event, fn) => el?.addEventListener(event, fn, { signal: this.controller.signal });
      on(this.repeatInput, 'change', () => { this.review.checked = false; this.render(); });
      on(this.fileInput, 'change', () => this.loadOwn());
      on(this.linkInput, 'input', () => { this.review.checked = false; if (!this.fileInput.files.length) this.clear(this.words('link')); });
      on(this.patternInput, 'input', () => { this.pattern = null; delete this.form.dataset.patternPreview; this.form.querySelector('[name="properties[_pattern_handle]"]').value = ''; this.clear(this.words('manual')); });
      this.form.querySelectorAll('[name="ns-artwork-mode"]').forEach(r => on(r, 'change', () => {
        this.clear(this.words('empty'));
        if (this.mode() === 'own') this.loadOwn();
        else if (this.pattern?.image) this.loadImage(this.pattern.image, 'library');
      }));
      on(document, 'niaash:pattern', e => { this.pattern = e.detail; if (this.mode() === 'pattern' && this.pattern.image) this.loadImage(this.pattern.image, 'library'); });
      on(this.form, 'submit', e => {
        let error = '';
        if (this.loading) error = this.words('loading');
        else if (this.fileError && this.mode() === 'own') error = this.fileError;
        else if (!this.review.checked) error = this.words('review');
        if (error) {
          e.preventDefault();
          const box = this.form.querySelector('[data-order-error]');
          box.textContent = error;
          box.focus();
          box.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
      this.clear(this.words('empty'));
      if (this.form.dataset.patternPreview) {
        this.pattern = { image: this.form.dataset.patternPreview };
        if (this.mode() === 'pattern') this.loadImage(this.pattern.image, 'library');
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.token++; if (this.objectUrl) URL.revokeObjectURL(this.objectUrl); }
    words(key) {
      const words = {
        empty: ['Choose a JPG/PNG file or a library pattern to see the layout.', 'Wybierz plik JPG/PNG lub wzór z biblioteki, aby zobaczyć układ.'],
        link: ['Download link attached. The source file needs a manual artwork review; no preview is available.', 'Dodano link. Plik wymaga ręcznego sprawdzenia; podgląd jest niedostępny.'],
        manual: ['Reference entered manually. Confirm the artwork with Niaash before production.', 'Wpisano numer ręcznie. Potwierdź projekt z Niaash przed produkcją.'],
        format: ['TIFF/PDF attached for manual review. Browser layout preview supports JPG and PNG.', 'Dodano TIFF/PDF do ręcznego sprawdzenia. Podgląd obsługuje JPG i PNG.'],
        invalid: ['This image could not be read. Choose a valid JPG/PNG or use a download link for manual review.', 'Nie można odczytać obrazu. Wybierz poprawny JPG/PNG lub podaj link do ręcznego sprawdzenia.'],
        type: ['Use JPG, PNG, TIFF or PDF.', 'Użyj JPG, PNG, TIFF lub PDF.'],
        size: ['This file exceeds 20 MB. Use a download link instead.', 'Plik przekracza 20 MB. Podaj link do pobrania.'],
        huge: ['This image is too large for a browser preview. Use a download link for manual review.', 'Obraz jest zbyt duży do podglądu. Podaj link do ręcznego sprawdzenia.'],
        range: ['Repeat width and height must be between 0.5 and 200 cm. Adjust the width for this image’s proportions.', 'Szerokość i wysokość raportu muszą wynosić od 0,5 do 200 cm. Dostosuj szerokość do proporcji obrazu.'],
        review: ['Confirm the layout / manual review acknowledgement before adding the fabric.', 'Potwierdź układ lub ręczne sprawdzenie przed dodaniem tkaniny.'],
        loading: ['Loading the preview. Please wait.', 'Trwa ładowanie podglądu. Poczekaj.'],
        library: ['Library reference preview. Source print resolution still requires verification.', 'Podgląd wzoru z biblioteki. Rozdzielczość pliku do druku wymaga weryfikacji.'],
        low: ['Low effective resolution at this scale. Use a smaller repeat or discuss a higher-resolution original.', 'Niska rozdzielczość w tej skali. Zmniejsz raport lub omów plik o wyższej rozdzielczości.'],
        medium: ['Check fine details on a test print before production.', 'Sprawdź drobne szczegóły na wydruku próbnym przed produkcją.'],
        high: ['Resolution estimate only. Colour, seams and print suitability still need review.', 'To oszacowanie rozdzielczości. Kolor, łączenia i przydatność do druku wymagają sprawdzenia.']
      };
      return words[key][this.lang === 'pl' ? 1 : 0];
    }
    clear(message) {
      this.token++;
      this.image = null;
      this.loading = false;
      this.review.checked = false;
      this.status.textContent = message;
      this.canvas.setAttribute('aria-label', message);
      this.hiddenStatus.value = 'Original artwork and print dimensions require confirmation';
      this.querySelector('[data-image-info]').textContent = '';
      this.drawGrid();
      const example=this.querySelector('[data-preview-example]');
      if(example){example.hidden=message!==this.words('empty');this.canvas.hidden=!example.hidden;}
    }
    loadOwn() {
      this.fileError = '';
      if (this.objectUrl) { URL.revokeObjectURL(this.objectUrl); this.objectUrl = null; }
      const file = this.fileInput.files[0];
      this.clear(this.words(this.linkInput.value ? 'link' : 'empty'));
      if (!file) return;
      if (file.size > 20 * 1024 * 1024) { this.fileError = this.words('size'); this.status.textContent = this.fileError; return; }
      const extension = file.name.split('.').pop().toLowerCase();
      if (!['jpg', 'jpeg', 'png', 'tif', 'tiff', 'pdf'].includes(extension)) { this.fileError = this.words('type'); this.status.textContent = this.fileError; return; }
      if (['tif', 'tiff', 'pdf'].includes(extension)) { this.status.textContent = this.words('format'); return; }
      this.objectUrl = URL.createObjectURL(file);
      this.loadImage(this.objectUrl, 'own');
    }
    loadImage(url, source) {
      const token = ++this.token;
      this.loading = true;
      this.review.checked = false;
      this.status.textContent = this.words('loading');
      const image = new Image();
      image.onload = () => {
        if (token !== this.token || !this.isConnected) return;
        this.loading = false;
        if (image.naturalWidth * image.naturalHeight > 40000000) { this.clear(this.words('huge')); this.fileError = this.words('huge'); return; }
        this.image = image;
        const example=this.querySelector('[data-preview-example]');if(example)example.hidden=true;this.canvas.hidden=false;
        this.source = source;
        this.render();
      };
      image.onerror = () => { if (token === this.token) { this.clear(this.words('invalid')); if (source === 'own') this.fileError = this.words('invalid'); } };
      image.src = url;
    }
    drawGrid() {
      const ctx = this.context, size = this.canvas.width;
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, size, size);
      ctx.strokeStyle = '#e3e8ee'; ctx.lineWidth = 1;
      for (let i = 0; i <= 10; i++) { const n = i * size / 10; ctx.beginPath(); ctx.moveTo(n, 0); ctx.lineTo(n, size); ctx.moveTo(0, n); ctx.lineTo(size, n); ctx.stroke(); }
      if (!this.image) { ctx.fillStyle='#f8f8f5';ctx.fillRect(70,size/2-55,size-140,110);ctx.fillStyle='#666660';ctx.font='22px Arial';ctx.textAlign='center';ctx.fillText(this.lang==='pl'?'Wybierz lub prześlij projekt':'Choose or upload your artwork',size/2,size/2);ctx.font='17px Arial';ctx.fillText(this.lang==='pl'?'Podgląd pojawi się tutaj':'Your layout will appear here',size/2,size/2+32); }
    }
    render() {
      this.drawGrid();
      if (!this.image) return;
      const aspect = this.image.naturalWidth / this.image.naturalHeight;
      const width = aspect > 1 ? 18 : 18 * aspect;
      const height = aspect > 1 ? 18 / aspect : 18;
      const tiles = window.NiaashPrintMath.tiles(width, height, this.repeatInput.value);
      if (!tiles.length) {
        const fit=Math.min(this.canvas.width/this.image.naturalWidth,this.canvas.height/this.image.naturalHeight);
        const w=this.image.naturalWidth*fit,h=this.image.naturalHeight*fit;
        this.context.drawImage(this.image,(this.canvas.width-w)/2,(this.canvas.height-h)/2,w,h);
      }
      const ctx = this.context, factor = this.canvas.width / 50;
      for (const tile of tiles) {
        ctx.save(); ctx.translate((tile.x + (tile.flipX ? width : 0)) * factor, (tile.y + (tile.flipY ? height : 0)) * factor);
        ctx.scale(tile.flipX ? -1 : 1, tile.flipY ? -1 : 1);
        ctx.drawImage(this.image, 0, 0, width * factor, height * factor); ctx.restore();
      }
      this.querySelector('[data-image-info]').textContent = this.source === 'own' ? this.image.naturalWidth + ' × ' + this.image.naturalHeight + ' px' : '';
      this.status.textContent = this.lang === 'pl' ? 'Podgląd poglądowy. Wymiary i kolory druku potwierdzamy osobno.' : 'Illustrative preview. Print dimensions and colour are confirmed separately.';
      this.hiddenStatus.value = 'Original artwork and print dimensions require confirmation';
      this.canvas.setAttribute('aria-label', (this.lang === 'pl' ? 'Raport prosty' : 'Straight repeat') + (this.lang === 'pl' ? ' — podgląd poglądowy' : ' — illustrative reference'));
    }
  }
  customElements.define('niaash-print-studio', PrintStudio);
})();
